/**
 * kisiKkdService.js — Kişi Detay › Kontroller sekmesindeki KKD (kişisel koruyucu donanım) onay listesi.
 * Backlog #374 (backend #373): "Yelek verildi" yerine, FUAR BAŞINA madde madde işaretlenen KKD.
 *
 * Bir kişinin bir fuardaki KKD'si: fuarın istediği maddeler (Yelek, Baret, Ayakkabı, ...) + personelin işaretledikleri.
 * `GET` KATALOGDAKİ HER AKTİF maddeyi döner (fuar istemese de; `seviye: 0`), kim/ne zaman işaretledi ile birlikte —
 * personel istemeyen maddeyi de işaretleyebilir. YALNIZCA SAKA personeli (firma 403). KKD kart kararına HİÇ girmez.
 *
 * Şekil (GET ve PATCH `data`sı aynı): `{ fuarId, istenen, durum UYGUN|UYARI|EKSIK|ISTENMIYOR, uygun, kalemler[{
 *   kkdKalemiId, kod, ad, ikon (lucide adı|null), sira, seviye 0|1|2, seviyeAdi, mevcut, isaretlendi,
 *   isaretleyenUserId, isaretleyenAdi, isaretlemeTarihi ("yyyy-MM-dd HH:mm:ss") }] }`.
 * ⚠️ GET zarfsız düz nesne döner, PATCH `{message, result, data}` zarfıdır.
 */
import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';

const BASE = `${JAVA_BACKEND_API_URL}/personnel`;

// Backend 400/500'ü bazen düz metin, bazen {message,...} zarfı döner; 409/400'de zarfın `data`sı `kod` taşıyabilir.
// Diğer servislerdeki aynı yardımcının kopyası (.status, .kod, .data).
function parseError(error) {
  const govde = error.response?.data;
  const message = !error.response
    ? 'Sunucu yanıt vermedi.'
    : typeof govde === 'string' && govde
      ? govde
      : govde?.message ?? error.message ?? 'İşlem başarısız oldu.';
  const wrapped = new Error(message);
  wrapped.status = error.response?.status ?? null;
  wrapped.data = govde?.data ?? null;
  wrapped.kod = wrapped.data?.kod ?? null;
  throw wrapped;
}

/**
 * Bir kişinin bir fuardaki KKD listesi (her aktif madde). Kişinin o fuarda görevli olması gerekmez.
 * 404: kişi ya da fuar yok.
 * GET /api/v1/personnel/{personId}/kkd?fuarId=
 */
export async function getKisiKkd(personId, fuarId) {
  try {
    const response = await axios.get(`${BASE}/${personId}/kkd`, { params: { fuarId }, headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    parseError(error);
  }
}

/**
 * KKD maddelerini işaretler / kaldırır. Kısmi: yalnızca verilen maddeler değişir, zaten o değerde olan madde yeniden
 * yazılmaz (kim/ne zaman son GERÇEK değişiklikte kalır). `kalemler`: `[{ kod, mevcut: true|false }]`.
 * Dönüş = GET şekli (yanıt zarfının `data`sı). 409: birleştirilmiş (gizli) kart · 400: bilinmeyen/kaldırılmış/tekrarlı madde.
 * PATCH /api/v1/personnel/{personId}/kkd?fuarId= — `{ kalemler: [{ kod, mevcut }] }`
 */
export async function setKisiKkd(personId, fuarId, kalemler) {
  let response;
  try {
    response = await axios.patch(
      `${BASE}/${personId}/kkd`,
      { kalemler },
      { params: { fuarId }, headers: getAuthHeader() },
    );
  } catch (error) {
    parseError(error);
  }
  // ⚠️ `result:false` HTTP 200 ile de gelebilir (diğer PATCH uçlarındaki gibi) — axios hata fırlatmaz.
  const govde = response.data;
  if (govde && typeof govde === 'object' && govde.result === false) {
    const hata = new Error(govde.message || 'İşlem başarısız oldu.');
    hata.status = response.status;
    throw hata;
  }
  return govde?.data ?? null;
}
